import { apiClient } from './api'
import { transformProject, transformProjectForBackend, transformProjectStats } from '../utils/apiTransformers'
import type { Priority } from './types'
import { track } from '../utils/analytics'

export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled'

export interface Project {
  id: number
  name: string
  description: string | null
  status: ProjectStatus
  priority: Priority
  start_date: string | null
  end_date: string | null
  project_manager_id: number | null
  budget: string | null
  actual_cost: string | null
  currency: string | null
  gl_code: string | null
  cost_center: string | null
  progress: number
  organization_id: number | null
  portfolio_id: number | null
  program_id: number | null
  // Part 21 — scope/objective scaffolding.
  objective?: string | null
  key_objectives?: string[] | null
  success_criteria?: string[] | null
  created_at: string
  updated_at: string
  metadata?: Record<string, unknown>
}

export interface ProjectCreate {
  name: string
  description?: string
  status?: ProjectStatus
  priority?: Priority
  start_date?: string
  end_date?: string
  project_manager_id?: number
  budget?: string
  organization_id?: number
  portfolio_id?: number
  program_id?: number
  team_ids?: number[]
  metadata?: Record<string, unknown>
}

export interface ProjectUpdate {
  name?: string
  description?: string
  status?: ProjectStatus
  priority?: Priority
  start_date?: string
  end_date?: string
  project_manager_id?: number
  budget?: string
  actual_cost?: string
  currency?: string
  gl_code?: string
  cost_center?: string
  progress?: number
  portfolio_id?: number
  program_id?: number
  team_ids?: number[]
  // Part 21 — scope/objective scaffolding.
  objective?: string | null
  key_objectives?: string[] | null
  success_criteria?: string[] | null
  metadata?: Record<string, unknown>
}

export interface ProjectStats {
  project_id: number
  total_tasks: number
  tasks_by_status: Record<string, number>
  progress: number
  budget: string | null
  actual_cost: string | null
}

export const projectsService = {
  /**
   * List projects
   */
  async list(params?: {
    project_manager_id?: number
    portfolio_id?: number
    program_id?: number
    status?: string
    skip?: number
    limit?: number
    include_deleted?: boolean
  }): Promise<Project[]> {
    // Filter out NaN values from params
    const cleanParams: Record<string, unknown> = {}
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = params[key as keyof typeof params]
        // Only include valid numbers (not NaN) and other valid values
        if (typeof value === 'number' && !isNaN(value)) {
          cleanParams[key] = value
        } else if (typeof value !== 'number') {
          cleanParams[key] = value
        }
      })
    }
    const response = await apiClient.get<unknown[]>('/api/v1/projects', { params: cleanParams })
    return response.data.map(transformProject)
  },

  /**
   * Get project by ID
   */
  async get(id: number): Promise<Project> {
    const response = await apiClient.get<unknown>(`/api/v1/projects/${id}`)
    return transformProject(response.data)
  },

  /**
   * Create project
   */
  async create(data: ProjectCreate): Promise<Project> {
    const transformedData = transformProjectForBackend(data)
    const response = await apiClient.post<unknown>('/api/v1/projects', transformedData)
    track('project_created')
    return transformProject(response.data)
  },

  /**
   * Update project
   */
  async update(id: number, data: ProjectUpdate): Promise<Project> {
    const transformedData = transformProjectForBackend(data)
    const response = await apiClient.put<unknown>(`/api/v1/projects/${id}`, transformedData)
    return transformProject(response.data)
  },

  /**
   * Delete project
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/projects/${id}`)
  },

  /**
   * Restore a soft-deleted project
   */
  async restore(id: number): Promise<Project> {
    const response = await apiClient.post<unknown>(`/api/v1/projects/${id}/restore`)
    return transformProject(response.data)
  },

  /**
   * Get project statistics
   */
  async getStats(id: number): Promise<ProjectStats> {
    const response = await apiClient.get<unknown>(`/api/v1/projects/${id}/stats`)
    return transformProjectStats(response.data)
  },

  /**
   * Single source of truth for project counts (total + per-status), computed
   * server-side over the unfiltered tenant set. Used by the portfolio status
   * cards and the dashboard so the two never disagree.
   */
  async summary(): Promise<ProjectSummary> {
    const response = await apiClient.get<ProjectSummary>('/api/v1/projects/summary')
    return response.data
  },
}

export interface ProjectSummary {
  total: number
  by_status: Record<string, number>
}

